import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import List from '@mui/joy/List/index.js'
import ListItem from '@mui/joy/ListItem/index.js'
import ListItemButton from '@mui/joy/ListItemButton/index.js'
import ListItemContent from '@mui/joy/ListItemContent/index.js'
import Typography from '@mui/joy/Typography/index.js'
import Sheet from '@mui/joy/Sheet/index.js'
import Box from '@mui/joy/Box/index.js'
import Button from '@mui/joy/Button/index.js'
import Chip from '@mui/joy/Chip/index.js'
import ChipDelete from '@mui/joy/ChipDelete/index.js'
import PersonIcon from '@mui/icons-material/Person'
import PeopleIcon from '@mui/icons-material/People'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import CircularProgress from '@mui/joy/CircularProgress'
import { get } from 'aws-amplify/api'
import UserSelector from './UserSelector.jsx'
import { fetchAuthSession } from 'aws-amplify/auth'

export default function ConversationList({
  onSelectConversation,
  admin,
  onSelectUser,
}) {
  const [selectedConversation, setSelectedConversation] = useState(undefined)
  const [conversations, setConversations] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [userSelectorOpen, setUserSelectorOpen] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const [nextToken, setNextToken] = useState(null)
  const [hasMore, setHasMore] = useState(true)

  const fetchConversations = async (
    userId = null,
    token = null,
    append = false,
  ) => {
    if (token) {
      setLoadingMore(true)
    } else {
      setLoading(true)
    }

    const s = await fetchAuthSession()
    const idToken = s.tokens?.idToken?.toString()

    try {
      const path =
        userId ?
          `/1/conversations?username=${encodeURIComponent(userId)}`
        : '/1/conversations'

      const queryParams =
        token ?
          {
            limit: '10',
            nextToken: token,
          }
        : {
            limit: '10',
          }

      const restOperation = get({
        apiName: 'technician-bot',
        path,
        options: {
          headers: {
            Authorization: idToken,
          },
          queryParams,
        },
      })

      const { body } = await restOperation.response
      const response = await body.json()

      // Update next token
      setNextToken(response.nextToken || null)
      setHasMore(!!response.nextToken)

      const mappedConversations = (response.conversations ?? [])
        .map((conversation) => ({
          id: conversation.conversationId,
          title: conversation.title,
          date: conversation.timestamp,
          author: conversation.userId,
          message: conversation.firstMessage,
          preview: conversation.firstMessage,
        }))
        .sort((a, b) => b.date - a.date)

      if (append) {
        setConversations((prev) => [...prev, ...mappedConversations])
      } else {
        setConversations(mappedConversations)
      }

      setLoading(false)
      setLoadingMore(false)
    } catch (error) {
      console.error('Error fetching conversations:', error)
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const loadMoreConversations = () => {
    if (nextToken && hasMore && !loadingMore) {
      void fetchConversations(currentUser?.username || null, nextToken, true)
    }
  }

  useEffect(() => {
    void fetchConversations(currentUser?.username)
  }, [currentUser])

  const handleSelectConversation = (c) => {
    setSelectedConversation(c.id)
    if (onSelectConversation) {
      onSelectConversation(c.id)
    }
  }

  const handleSelectUser = (user) => {
    setCurrentUser(user)
    // Pass the selected user up to the parent component
    if (onSelectUser) {
      onSelectUser(user)
    }
    setNextToken(null)
    setHasMore(true)
    void fetchConversations(user.username)
  }

  const resetToCurrentUser = () => {
    setCurrentUser(null)
    // Notify parent that user selection was reset
    if (onSelectUser) {
      onSelectUser(null)
    }
    setNextToken(null)
    setHasMore(true)
    void fetchConversations()
  }

  // Handler for scroll events to implement infinite scrolling
  const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 200
    if (bottom && hasMore && !loadingMore && !loading) {
      loadMoreConversations()
    }
  }

  return (
    <>
      <Sheet
        sx={{
          height: '100%',
          width: '400px',
          borderRight: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.surface',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography level="title-lg">Conversations</Typography>
            {admin && (
              <Button
                startDecorator={<PeopleIcon />}
                size="sm"
                onClick={() => {
                  setUserSelectorOpen(true)
                }}
                variant="outlined"
              >
                Select User
              </Button>
            )}
          </Box>

          {currentUser && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                startDecorator={<PersonIcon />}
                size="md"
                color="primary"
                variant="soft"
                endDecorator={<ChipDelete onDelete={resetToCurrentUser} />}
              >
                {currentUser.username || currentUser.userId}
              </Chip>
            </Box>
          )}
        </Box>

        <List
          sx={{
            flex: 1,
            overflowY: 'auto',
          }}
          onScroll={handleScroll}
        >
          {loading ?
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <CircularProgress size="sm" />
              <Typography level="body-sm" sx={{ mt: 1 }}>
                Loading conversations...
              </Typography>
            </Box>
          : conversations.length === 0 ?
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography level="body-sm">
                {currentUser ?
                  'No conversations for this user'
                : 'No conversations found'}
              </Typography>
            </Box>
          : <>
              {conversations.map((conversation) => (
                <ListItem key={conversation.id}>
                  <ListItemButton
                    selected={selectedConversation === conversation.id}
                    onClick={() => {
                      handleSelectConversation(conversation)
                    }}
                    sx={{
                      p: 2,
                      borderRadius: 'sm',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.softBg',
                      },
                    }}
                  >
                    <ListItemContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <Typography level="title-sm" noWrap>
                          {conversation.title}
                        </Typography>
                        <Typography
                          level="body-xs"
                          sx={{ color: 'text.tertiary' }}
                        >
                          {dayjs(parseInt(conversation.date)).format(
                            'MMM D, YYYY',
                          )}
                        </Typography>
                      </Box>
                      <Typography
                        level="body-sm"
                        noWrap
                        sx={{ color: 'text.secondary' }}
                      >
                        {conversation.preview}
                      </Typography>
                    </ListItemContent>
                  </ListItemButton>
                </ListItem>
              ))}

              {/* Show loading indicator when fetching more conversations */}
              {loadingMore && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <CircularProgress size="sm" />
                </Box>
              )}

              {/* Load more button - alternative to infinite scroll */}
              {!loadingMore && hasMore && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Button
                    onClick={loadMoreConversations}
                    startDecorator={<MoreHorizIcon />}
                    size="sm"
                    variant="soft"
                  >
                    Load more
                  </Button>
                </Box>
              )}
            </>
          }
        </List>
      </Sheet>

      <UserSelector
        isOpen={userSelectorOpen}
        onClose={() => {
          setUserSelectorOpen(false)
        }}
        onSelectUser={handleSelectUser}
      />
    </>
  )
}
